import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Typography, Space, Avatar, Tag, Statistic, Progress,
  Select, Button, Empty, Spin, Table, Divider,
} from 'antd';
import {
  ArrowLeftOutlined, RobotOutlined, GlobalOutlined, ThunderboltOutlined,
  MailOutlined, FacebookOutlined, ReadOutlined, VideoCameraOutlined,
  GoogleOutlined, FileTextOutlined, RiseOutlined, TrophyOutlined,
} from '@ant-design/icons';
import { aiSalesAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

const COUNTRY_OPTIONS = [
  { code: 'TW', label: '台灣', flag: '🇹🇼' },
  { code: 'TH', label: '泰國', flag: '🇹🇭' },
  { code: 'VN', label: '越南', flag: '🇻🇳' },
  { code: 'AU', label: '澳洲', flag: '🇦🇺' },
];
const countryLabel = (code) => {
  const c = COUNTRY_OPTIONS.find(o => o.code === code);
  return c ? `${c.flag} ${c.label}` : (code || '—');
};

const PERIOD_OPTIONS = [
  { value: 'week', label: '本周' },
  { value: 'month', label: '本月' },
  { value: 'quarter', label: '本季' },
  { value: 'all', label: '累計' },
];

// Asset type → icon + display config
const ASSET_TYPES = {
  blog_post:    { label: 'Blog Post',     icon: <ReadOutlined />,         color: '#fa8c16' },
  video:        { label: 'Video (AI)',    icon: <VideoCameraOutlined />,  color: '#eb2f96' },
  fb_post:      { label: 'Facebook Post', icon: <FacebookOutlined />,     color: '#4267B2' },
  google_ads:   { label: 'Google Ads',    icon: <GoogleOutlined />,       color: '#4285F4' },
  email_campaign: { label: 'Email',       icon: <MailOutlined />,         color: '#1890ff' },
  other:        { label: 'Other',         icon: <FileTextOutlined />,     color: '#52c41a' },
};

const CHANNEL_LABELS = {
  email_blast: 'Email 群發',
  fb_post: 'Facebook 貼文',
  blog_post: '部落格文章',
  video_generate: 'AI 影片',
  google_ads_create: 'Google Ads',
  generate_content: '通用內容',
  sale: '成交',
  inquiry: '詢問',
  site_visit: '站內瀏覽',
};

// ── Funnel pyramid component ────────────────────────────────
function FunnelBlock({ funnel }) {
  const stages = [
    { key: 'outreach', label: '內容發佈', color: '#1890ff', count: funnel.outreach },
    { key: 'inquiry',  label: '客戶詢問', color: '#faad14', count: funnel.inquiry },
    { key: 'sale',     label: '成交',     color: '#52c41a', count: funnel.sale },
  ];
  const max = Math.max(...stages.map(s => s.count), 1);
  return (
    <Card title={<Space><RiseOutlined /> 銷售漏斗</Space>} size="small">
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {stages.map((s, i) => {
          const widthPct = Math.max((s.count / max) * 100, 8);
          const prev = i > 0 ? stages[i - 1].count : null;
          const conv = (prev !== null && prev > 0)
            ? ((s.count / prev) * 100).toFixed(1) + '%' : null;
          return (
            <div key={s.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 80, textAlign: 'right' }}>
                  <Text strong>{s.label}</Text>
                </div>
                <div style={{ flex: 1, height: 36, background: '#f5f5f5', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                  <div style={{
                    width: `${widthPct}%`, height: '100%',
                    background: `linear-gradient(90deg, ${s.color}, ${s.color}cc)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    paddingRight: 12, transition: 'width 0.3s',
                  }}>
                    <Text strong style={{ color: '#fff', fontSize: 14 }}>{s.count}</Text>
                  </div>
                </div>
                <div style={{ width: 80 }}>
                  {conv ? <Tag color={s.color}>轉化 {conv}</Tag> : null}
                </div>
              </div>
            </div>
          );
        })}
      </Space>
    </Card>
  );
}

// ── Channel breakdown (sankey-style horizontal bars) ────────
function ChannelBlock({ breakdown }) {
  const entries = Object.entries(breakdown || {}).sort((a, b) => b[1].count - a[1].count);
  if (entries.length === 0) return null;
  const maxCount = Math.max(...entries.map(([, v]) => v.count), 1);
  return (
    <Card title={<Space><ThunderboltOutlined /> 各 channel 使用次數 & 貢獻</Space>} size="small">
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {entries.map(([key, v]) => {
          const widthPct = Math.max((v.count / maxCount) * 100, 5);
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 120, fontSize: 13 }}>
                {CHANNEL_LABELS[key] || key}
              </div>
              <div style={{ flex: 1, height: 28, background: '#f5f5f5', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  width: `${widthPct}%`, height: '100%',
                  background: 'linear-gradient(90deg, #722ed1, #eb2f96)',
                  display: 'flex', alignItems: 'center', paddingLeft: 10,
                }}>
                  <Text style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{v.count} 次</Text>
                </div>
              </div>
              <div style={{ width: 180, textAlign: 'right' }}>
                <Text style={{ color: '#52c41a', fontSize: 13 }}>
                  +NT${v.revenue.toLocaleString()}
                </Text>
                <div style={{ fontSize: 11 }}>
                  <Text type="secondary">cost ${v.cost.toFixed(0)}</Text>
                  {v.tokens > 0 && (
                    <Text style={{ marginLeft: 8, color: '#b37feb' }}>
                      🔮 {v.tokens.toLocaleString()} tok
                    </Text>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </Space>
    </Card>
  );
}

// ── Timeline (daily activity + cumulative SVG line) ────────
function TimelineBlock({ timeline }) {
  if (!timeline || timeline.length === 0) {
    return (
      <Card title={<Space><FileTextOutlined /> 每日活動與累計營收</Space>} size="small">
        <Empty description="No timeline data" />
      </Card>
    );
  }
  const W = 800, H = 180, P = 24;
  const innerW = W - P * 2, innerH = H - P * 2;
  const maxActivity = Math.max(...timeline.map(d => d.activity_count), 1);
  const maxCumulative = Math.max(...timeline.map(d => d.cumulative_revenue), 1);
  const colW = innerW / timeline.length;

  const linePoints = timeline.map((d, i) => {
    const x = P + colW * i + colW / 2;
    const y = P + innerH - (d.cumulative_revenue / maxCumulative) * innerH;
    return `${x},${y}`;
  }).join(' ');

  return (
    <Card title={<Space><FileTextOutlined /> 每日活動 & 累計營收</Space>} size="small">
      <div style={{ overflowX: 'auto' }}>
        <svg width={W} height={H} style={{ display: 'block', minWidth: W }}>
          {/* Activity bars */}
          {timeline.map((d, i) => {
            const x = P + colW * i + colW * 0.15;
            const w = colW * 0.7;
            const h = (d.activity_count / maxActivity) * innerH;
            const y = P + innerH - h;
            return (
              <g key={d.date}>
                <rect x={x} y={y} width={w} height={h} fill="#1890ff" opacity={0.6} rx={2} />
                <title>{d.date}: {d.activity_count} 次活動 / 當日 NT${d.revenue.toLocaleString()}</title>
              </g>
            );
          })}
          {/* Cumulative revenue line */}
          <polyline fill="none" stroke="#52c41a" strokeWidth={2.5} points={linePoints} />
          {timeline.map((d, i) => {
            const x = P + colW * i + colW / 2;
            const y = P + innerH - (d.cumulative_revenue / maxCumulative) * innerH;
            return <circle key={d.date} cx={x} cy={y} r={3} fill="#52c41a">
              <title>{d.date}: 累計 NT${d.cumulative_revenue.toLocaleString()}</title>
            </circle>;
          })}
          {/* x-axis dates (sparse) */}
          {timeline.map((d, i) => {
            if (timeline.length > 10 && i % Math.ceil(timeline.length / 8) !== 0) return null;
            const x = P + colW * i + colW / 2;
            return (
              <text key={d.date} x={x} y={H - 4} fontSize={10} fill="#999" textAnchor="middle">
                {d.date.slice(5)}
              </text>
            );
          })}
        </svg>
      </div>
      <Space size={16} style={{ marginTop: 8 }}>
        <Tag color="blue">藍柱 = 當日活動次數</Tag>
        <Tag color="green">綠線 = 累計營收</Tag>
      </Space>
    </Card>
  );
}

// ── Assets list grouped by type ────────────────────────────
function AssetsBlock({ assetsByType }) {
  const types = Object.keys(assetsByType || {});
  if (types.length === 0) {
    return (
      <Card title={<Space><FileTextOutlined /> 業務產出的內容資產</Space>} size="small">
        <Empty description="尚未產生任何內容" />
      </Card>
    );
  }
  return (
    <Card title={<Space><FileTextOutlined /> 業務產出的內容資產</Space>} size="small">
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {types.map(type => {
          const cfg = ASSET_TYPES[type] || ASSET_TYPES.other;
          const items = assetsByType[type];
          return (
            <div key={type}>
              <div style={{ marginBottom: 8 }}>
                <Tag color={cfg.color} icon={cfg.icon} style={{ fontSize: 13, padding: '2px 10px' }}>
                  {cfg.label} ({items.length})
                </Tag>
              </div>
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={items}
                columns={[
                  {
                    title: 'Title', dataIndex: 'title', ellipsis: true,
                    render: (v, r) => r.asset_url
                      ? <a href={r.asset_url} target="_blank" rel="noopener noreferrer">{v}</a>
                      : v,
                  },
                  {
                    title: 'Status', dataIndex: 'status', width: 100,
                    render: s => <Tag color={s === 'published' ? 'green' : s === 'queued' ? 'blue' : s === 'failed' ? 'red' : 'default'}>{s}</Tag>,
                  },
                  {
                    title: 'Revenue', dataIndex: 'revenue_amount', width: 120, align: 'right',
                    render: v => v > 0 ? <Text style={{ color: '#52c41a' }}>NT${v.toLocaleString()}</Text> : '-',
                  },
                  {
                    title: 'Cost', dataIndex: 'cost_amount', width: 90, align: 'right',
                    render: v => v > 0 ? `$${v.toLocaleString()}` : '-',
                  },
                  {
                    title: 'Created', dataIndex: 'created_at', width: 130,
                    render: v => v ? new Date(v).toLocaleDateString('zh-TW') : '-',
                  },
                ]}
              />
            </div>
          );
        })}
      </Space>
    </Card>
  );
}

export default function BotDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('quarter');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aiSalesAPI.botDashboard(id, { period });
      setData(res.data || null);
    } catch { /* ignore */ }
    setLoading(false);
  }, [id, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '120px auto' }} />;
  if (!data || data.error) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Empty description={data?.error || 'Bot not found'} />
        <Button onClick={() => navigate('/ai-sales')} style={{ marginTop: 16 }}>
          <ArrowLeftOutlined /> Back to AI Sales
        </Button>
      </div>
    );
  }

  const { bot, summary, funnel, channel_breakdown, timeline, assets_by_type } = data;

  return (
    <div>
      {/* Header */}
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/ai-sales')}>
          Back
        </Button>
        <Select size="small" value={period} onChange={setPeriod}
          options={PERIOD_OPTIONS} style={{ width: 110 }} />
      </Space>

      {/* Hero card */}
      <Card style={{ marginBottom: 16, background: 'linear-gradient(135deg, #001529, #003a8c)' }}>
        <Row align="middle" gutter={24}>
          <Col>
            <Avatar size={84} src={bot.avatar_url} icon={<RobotOutlined />}
              style={{ background: '#722ed1', border: '3px solid #faad14' }} />
          </Col>
          <Col flex="auto">
            <Title level={2} style={{ color: '#fff', margin: 0 }}>
              {bot.name}
            </Title>
            <Space size={8} style={{ marginTop: 4 }}>
              {bot.country_code && <Tag color="gold">{countryLabel(bot.country_code)}</Tag>}
              <Tag color={bot.status === 'active' ? 'green' : 'default'}>{bot.status}</Tag>
              {bot.competition_id && <Tag color="blue">Competition #{bot.competition_id}</Tag>}
            </Space>
            {bot.personality && (
              <Paragraph style={{ color: 'rgba(255,255,255,0.7)', margin: '8px 0 0', maxWidth: 600 }}>
                {bot.personality}
              </Paragraph>
            )}
          </Col>
          <Col>
            <Space size={24}>
              <Statistic title={<Text style={{ color: 'rgba(255,255,255,0.65)' }}>Revenue</Text>}
                value={summary.total_revenue} prefix="NT$"
                valueStyle={{ color: '#52c41a', fontSize: 22 }} />
              <Statistic title={<Text style={{ color: 'rgba(255,255,255,0.65)' }}>Cost</Text>}
                value={summary.total_cost} prefix="$"
                valueStyle={{ color: '#faad14', fontSize: 20 }} />
              <Statistic title={<Text style={{ color: 'rgba(255,255,255,0.65)' }}>Tokens</Text>}
                value={(summary.total_tokens || 0).toLocaleString()}
                valueStyle={{ color: '#b37feb', fontSize: 20 }} />
              {summary.roi_pct != null && (
                <Statistic title={<Text style={{ color: 'rgba(255,255,255,0.65)' }}>ROI</Text>}
                  value={summary.roi_pct} suffix="%"
                  valueStyle={{ color: '#1890ff', fontSize: 20 }} />
              )}
            </Space>
          </Col>
        </Row>
        {(bot.target_amount || 0) > 0 && (
          <div style={{ marginTop: 20 }}>
            <Text style={{ color: 'rgba(255,255,255,0.75)' }}>
              目標進度：NT${summary.total_revenue.toLocaleString()} / NT${bot.target_amount.toLocaleString()}
            </Text>
            <Progress
              percent={Math.min(summary.progress_pct, 100)}
              strokeColor={summary.progress_pct >= 100 ? '#52c41a' : summary.progress_pct >= 70 ? '#1890ff' : '#faad14'}
              trailColor="rgba(255,255,255,0.15)"
              showInfo
              format={pct => <Text style={{ color: '#fff' }}>{pct}%</Text>}
            />
          </div>
        )}
      </Card>

      {/* Funnel + Channel side-by-side on wide screens */}
      <Row gutter={16}>
        <Col xs={24} lg={10}>
          <FunnelBlock funnel={funnel} />
        </Col>
        <Col xs={24} lg={14}>
          <div style={{ marginTop: 0 }}>
            <ChannelBlock breakdown={channel_breakdown} />
          </div>
        </Col>
      </Row>

      <Divider style={{ margin: '24px 0 16px' }} />
      <TimelineBlock timeline={timeline} />

      <Divider style={{ margin: '24px 0 16px' }} />
      <AssetsBlock assetsByType={assets_by_type} />
    </div>
  );
}
